import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import {
  ConfirmButton,
  EmptyIndicator,
  ModalButtonWithForm,
  MoreOptions,
  Owners,
  TitleWithIcon
} from 'components/common';
import { Box, BoxRow } from 'components/common/box';
import { FormGroup, Select } from 'components/common/form';

import type { Group } from 'shared-types';
import type { GetProductVisibilityCriteriaRes } from 'shared-types/responses';

interface Props {
  disabledOverlayContent?: string;
  groups: GetProductVisibilityCriteriaRes['criteria']['groups'];
  loading: boolean;
  onAddGroup: (values: { groupId: number }) => Promise<void>;
  onRemoveGroup: (id: number) => Promise<boolean>;
}

const AccessAndVisibilityCriteriaGroups = ({
  disabledOverlayContent,
  groups,
  loading,
  onAddGroup,
  onRemoveGroup
}: Props) => {
  const { messages } = useReduxState();

  const { data } = useApiData({
    url: '/admin/groups-available'
  });

  const existingGroupIds = groups?.map((group) => group.group.id) || [];

  const groupOptions =
    data?.groups
      .filter((group: Group) => !existingGroupIds.includes(group.id))
      .map((group: Group) => ({
        label: group.title,
        value: group.id
      })) || [];

  return (
    <Box
      disabledOverlayContent={disabledOverlayContent}
      headerContent={
        <ModalButtonWithForm
          buttonCategory="success"
          buttonIcon="plus"
          buttonSize="header"
          buttonText={messages.commonAddGroup}
          modalTitle={messages.commonAddGroup}
          modalActionText={messages.commonAdd}
          onSubmit={onAddGroup}
          pristineIsValid={false}
        >
          {() => (
            <FormGroup label={messages.commonGroup}>
              <Select
                id="groupId"
                isSearchable={true}
                field="groupId"
                options={groupOptions}
                placeholder={messages.groupsSearch}
              />
            </FormGroup>
          )}
        </ModalButtonWithForm>
      }
      loading={loading}
      padding={0}
      title={messages.commonGroups}
    >
      {!groups?.length ? (
        <EmptyIndicator
          icon="users"
          size="small"
          title={messages.groupsEmpty}
        />
      ) : (
        groups.map((group) => (
          <BoxRow key={group.id}>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 10,
                justifyContent: 'space-between'
              }}
            >
              <TitleWithIcon
                icon="users"
                title={group.group.title}
                titleLink={{
                  as: `/groups/${group.group.id}`,
                  href: '/groups/[groupId]'
                }}
              >
                <Owners owners={group.group.owners} />
              </TitleWithIcon>

              <MoreOptions>
                <ConfirmButton
                  actionText={messages.commonRemove}
                  buttonText={messages.commonRemoveGroup}
                  category="danger"
                  confirmText={messages.commonRemoveGroupConfirm}
                  confirmTitle={messages.commonRemoveGroup}
                  onConfirm={() => onRemoveGroup(group.id)}
                  successMessage={messages.commonRemoveGroupSuccess}
                />
              </MoreOptions>
            </div>
          </BoxRow>
        ))
      )}
    </Box>
  );
};

export default AccessAndVisibilityCriteriaGroups;
